/**
 * Structured logger for web app.
 * Uses Sentry for error tracking and structured console output for development.
 *
 * Usage:
 *   import { logger } from './lib/logger';
 *   logger.error('Failed to load data', error, { userId: '123' });
 *   logger.warn('Deprecated API used', { endpoint: '/old' });
 *   logger.info('User logged in', { method: 'magic-link' });
 *   logger.debug('Cache hit', { key: 'user-123' });
 */
import { captureException, captureMessage, addBreadcrumb } from './sentry';
import { classifyError, getUserMessage, serializeError, type ErrorContext } from '@pantry/shared';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Only show debug logs in development
const MIN_LOG_LEVEL: LogLevel = import.meta.env.DEV ? 'debug' : 'info';

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[MIN_LOG_LEVEL];
}

function formatLogMessage(level: LogLevel, message: string, context?: LogContext): string {
  const timestamp = new Date().toISOString();
  const contextStr = context ? ` ${JSON.stringify(context)}` : '';
  return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
}

/**
 * Log a debug message (development only).
 */
function debug(message: string, context?: LogContext): void {
  if (!shouldLog('debug')) return;

  console.debug(formatLogMessage('debug', message, context));
  addBreadcrumb(message, 'debug', context, 'debug');
}

/**
 * Log an info message.
 */
function info(message: string, context?: LogContext): void {
  if (!shouldLog('info')) return;

  console.info(formatLogMessage('info', message, context));
  addBreadcrumb(message, 'info', context, 'info');
}

/**
 * Log a warning message.
 */
function warn(message: string, context?: LogContext): void {
  if (!shouldLog('warn')) return;

  console.warn(formatLogMessage('warn', message, context));
  captureMessage(message, 'warning');
}

/**
 * Log an error with optional exception.
 * Automatically sends to Sentry in production.
 */
function error(message: string, err?: unknown, context?: LogContext): void {
  if (!shouldLog('error')) return;

  const errorContext: ErrorContext = err
    ? classifyError(err)
    : { category: 'unknown', severity: 'error' };

  console.error(
    formatLogMessage('error', message, {
      ...context,
      errorCategory: errorContext.category,
      errorSeverity: errorContext.severity,
      ...(err ? serializeError(err) : {}),
    })
  );

  if (err) {
    captureException(err, {
      tags: {
        category: errorContext.category,
        severity: errorContext.severity,
      },
      extra: {
        message,
        ...context,
      },
    });
  } else {
    captureMessage(message, 'error');
  }
}

/**
 * Get a user-friendly message for an error.
 * Use this for displaying errors in the UI.
 */
function userMessage(err: unknown): string {
  return getUserMessage(err);
}

export const logger = {
  debug,
  info,
  warn,
  error,
  userMessage,
};

export type { LogContext, LogLevel };
